import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Home, User, Briefcase, Mail, Sun, Moon } from "lucide-react";
import { useThemeContext } from "../../App";

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.container};
  transition: all 0.3s ease;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  height: 56px; // AWS standard header height
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${props => props.theme.fonts.primary};
  font-weight: 700;
  font-size: 18px;
  color: ${props => props.theme.colors.textPrimary};
  text-decoration: none;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  .aws-logo {
    color: ${props => props.theme.colors.primary};
    font-weight: 900;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: prop => prop !== "active",
})`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${props => props.theme.borderRadius.small};
  color: ${props =>
    props.active
      ? props.theme.colors.primary
      : props.theme.colors.textSecondary};
  background: ${props =>
    props.active ? props.theme.colors.active : "transparent"};
  font-size: 14px;
  font-weight: ${props => (props.active ? "500" : "400")};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
    background: ${props => props.theme.colors.hover};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ThemeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borderRadius.small};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.textPrimary};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borderRadius.small};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.textPrimary};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 57px; // Header height + 1px border
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.dropdown};
  padding: 16px 24px;
  display: none;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 1200px;
  margin: 0 auto;
`;

const MobileNavLink = styled(Link).withConfig({
  shouldForwardProp: prop => prop !== "active",
})`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${props => props.theme.borderRadius.small};
  color: ${props =>
    props.active
      ? props.theme.colors.primary
      : props.theme.colors.textSecondary};
  background: ${props =>
    props.active ? props.theme.colors.active : "transparent"};
  font-size: 14px;
  font-weight: ${props => (props.active ? "500" : "400")};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
    background: ${props => props.theme.colors.hover};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MobileThemeToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: 8px;

  span {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 14px;
  }
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useThemeContext();

  const navItems = [
    { path: "/", label: "Home", icon: Home },
    { path: "/about", label: "About", icon: User },
    { path: "/projects", label: "Projects", icon: Briefcase },
    { path: "/contact", label: "Contact", icon: Mail },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  };

  return (
    <HeaderWrapper>
      <Nav>
        <Logo to="/">
          <span className="aws-logo">Portfolio</span>
        </Logo>

        <NavLinks>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink key={path} to={path} active={location.pathname === path}>
              <Icon />
              {label}
            </NavLink>
          ))}
        </NavLinks>

        <HeaderActions>
          <ThemeToggle onClick={toggleTheme} title="Toggle theme">
            {isDarkMode ? <Sun /> : <Moon />}
          </ThemeToggle>

          <MobileMenuButton onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </MobileMenuButton>
        </HeaderActions>
      </Nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <MobileNavLinks>
              {navItems.map(({ path, label, icon: Icon }) => (
                <MobileNavLink
                  key={path}
                  to={path}
                  active={location.pathname === path}
                  onClick={closeMobileMenu}
                >
                  <Icon />
                  {label}
                </MobileNavLink>
              ))}
            </MobileNavLinks>

            <MobileThemeToggle>
              <span>Dark mode</span>
              <button className="theme-toggle" onClick={toggleTheme}>
                {isDarkMode ? <Sun /> : <Moon />}
              </button>
            </MobileThemeToggle>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderWrapper>
  );
};

export default Header;
